<template>
  <article class="group-card" :class="cardClass">
    <header class="group-card__header">
      <p v-if="group.subtitle" class="group-card__subtitle" :title="group.subtitle">
        {{ group.subtitle }}
      </p>
      <h2 class="group-card__title" :title="group.title">
        {{ group.title }}
      </h2>
      <p class="group-card__state">{{ stateLabel }}</p>
    </header>

    <ul class="group-card__assets">
      <li
        v-for="asset in group.assets"
        :key="asset.key"
        class="group-card__asset"
      >
        <asset-band
          :asset="asset"
          :chart="chart"
          :hide-name="hideBandNames"
        />
      </li>
    </ul>
  </article>
</template>

<script>
import { DEFAULT_PANEL_CHART } from '@/domain/enums/panelChart';
import { getPulsePhase } from '@/domain/services/generalPanel/communicationStatusPanel/alarmPulse';
import AssetBand from './AssetBand';

export default {
  name: 'GroupCard',
  components: {
    AssetBand,
  },
  props: {
    /** Cartão devolvido por `groupAssets`: empresa, instalação ou ativo. */
    group: {
      type: Object,
      required: true,
    },
    chart: {
      type: String,
      default: DEFAULT_PANEL_CHART,
    },
    /** No agrupamento por ativo o nome da faixa repetiria o título do cartão. */
    hideBandNames: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    cardClass() {
      return [
        `group-card--${this.group.state}`,
        {
          'group-card--matched': this.group.matched,
          'group-card--dimmed': this.group.dimmed,
        },
        this.pulseClass,
      ];
    },
    /**
     * Pulsação de Alarme: marca mudança, não estado — pulsa o cartão cujo Ativo
     * trocou de estado neste ciclo, até o próximo. Cartões fora do filtro não
     * pulsam: já estão esmaecidos e o movimento só disputaria atenção com os
     * que casaram.
     */
    pulseClass() {
      if (!this.group.changed || this.group.dimmed) {
        return null;
      }

      return `group-card--pulsing group-card--phase-${getPulsePhase(this.group.key)}`;
    },
    /**
     * O estado também aparece como texto: em uma parede vista de longe, a cor
     * sozinha não sobrevive a daltonismo nem a TVs mal calibradas.
     */
    stateLabel() {
      return this.$t(`communication-status-panel.state.${this.group.state}`);
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/components/communication-status-panel-fluid"

// A camada de brilho precisa de um contexto de posicionamento no cartão.
.group-card
  position: relative
  // O cartão é a régua do texto: título, estado e as faixas dentro dele medem
  // uma fração da largura do cartão, e crescem quando a grade o alarga.
  container-type: inline-size
  background: var(--panel-surface)
  border: 1px solid var(--panel-border)
  border-left: 4px solid var(--panel-out-of-service)
  border-radius: 4px
  display: flex
  flex-direction: column
  gap: 0.5rem
  padding: 0.6rem 0.7rem
  // Só cor e sombra animam: o cartão nunca muda de posição nem de tamanho.
  transition-property: border-color, background-color, box-shadow
  transition-duration: 240ms
  transition-timing-function: ease-out

  &__header
    min-width: 0

  // Coeficientes em `cqw` calibrados para dar o tamanho fixo no cartão mais
  // estreito da grade (~212px úteis): dali para cima, só cresce.
  &__subtitle
    color: var(--panel-text-muted)
    +panel-fluid(font-size, 0.72rem, 5.4cqw, 1.2rem)
    letter-spacing: 0.06em
    margin: 0
    overflow: hidden
    text-overflow: ellipsis
    text-transform: uppercase
    white-space: nowrap

  &__title
    color: var(--panel-text)
    +panel-fluid(font-size, 1.05rem, 7.9cqw, 1.9rem)
    font-weight: 700
    line-height: 1.2
    margin: 0
    overflow: hidden
    text-overflow: ellipsis
    white-space: nowrap

  &__state
    +panel-fluid(font-size, 0.75rem, 5.7cqw, 1.3rem)
    font-weight: 600
    letter-spacing: 0.04em
    margin: 0.1rem 0 0
    text-transform: uppercase

  &__assets
    display: flex
    flex-direction: column
    gap: 0.45rem
    list-style: none
    margin: 0
    padding: 0

  &--attention
    background: var(--panel-attention-wash)
    border-color: var(--panel-attention)
    border-left-color: var(--panel-attention)
    .group-card__state
      color: var(--panel-attention)

  &--normal
    border-left-color: var(--panel-normal)
    .group-card__state
      color: var(--panel-normal)

  &--out-of-service
    border-left-color: var(--panel-out-of-service)
    opacity: 0.72
    .group-card__state
      color: var(--panel-out-of-service)

  // Realce do filtro. Canal próprio — contorno e elevação — para não colidir com
  // a dessaturação de Fora de Operação nem com a de Idade do Dado.
  &--matched
    box-shadow: 0 0 0 2px var(--panel-match), 0 4px 14px var(--panel-card-shadow)
    opacity: 1

  // Fora do filtro. Vai bem além do 0.72 de Fora de Operação para que os dois
  // níveis de apagamento não se confundam entre si.
  &--dimmed
    filter: grayscale(0.85)
    opacity: 0.28

  // Pulsação de Alarme.
  //
  // O brilho vive num pseudo-elemento e só o `opacity` dele anima: fica no
  // compositor, sem repaint, independente de quantos cartões pulsem ao mesmo
  // tempo. A sombra em si é pintada uma única vez.
  //
  // Sem `will-change`: com dezenas de cartões pulsando, promover cada um a
  // camada própria custaria mais memória do que o ganho — o navegador já promove
  // sozinho o que anima opacity.
  &--pulsing::after
    animation: group-card-pulse 2.4s ease-in-out infinite
    border-radius: inherit
    bottom: 0
    content: ''
    left: 0
    // Lados explícitos em vez de `inset`: a TV pode rodar um navegador
    // embarcado antigo, e `inset` não tem fallback nem prefixo.
    pointer-events: none
    position: absolute
    right: 0
    top: 0

  &--attention.group-card--pulsing::after
    box-shadow: var(--panel-pulse-attention)

  // Recuperar também é notícia, e pulsar vermelho nesse caso mentiria.
  &--normal.group-card--pulsing::after
    box-shadow: var(--panel-pulse-normal)

  &--out-of-service.group-card--pulsing::after
    box-shadow: var(--panel-pulse-out-of-service)

  // Defasagem derivada da chave do cartão, não da posição na grade: filtrar
  // reordena os cartões, e uma defasagem posicional se reembaralharia junto.
  @for $phase from 0 through 7
    &--phase-#{$phase}::after
      animation-delay: #{$phase * 0.3}s

@keyframes group-card-pulse
  0%,
  100%
    opacity: 0.15
  50%
    opacity: 1

// Pulsação é exatamente o estímulo que esta preferência existe para desligar.
// Nada se perde: cor e rótulo de texto já comunicam o estado.
@media (prefers-reduced-motion: reduce)
  .group-card--pulsing::after
    animation: none
    opacity: 0.5
</style>
